import { useCallback, useMemo, useRef, useState, type DragEvent } from "react";
import {
  Background, BackgroundVariant, ConnectionLineType, Controls, MarkerType, MiniMap, ReactFlow, ViewportPortal, useReactFlow,
  type Connection, type Edge, type EdgeChange, type Node, type NodeChange, type NodePositionChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useLocale, useTranslation } from "@ncp/i18n";
import { toast } from "@ncp/ui";
import { BuilderNode, LaneBand, type BuilderNodeData, type DiffStatus } from "./nodes";
import { BuilderEdge, type BuilderEdgeData } from "./edges";
import { CanvasMenu, type MenuState } from "./CanvasMenu";
import { DEFAULT_LANES, GRID, LANE_H, NODE_H, NODE_W, insertOnEdge, laneOf, newEdge, newNode, snap } from "../graph";
import { useBuilder, useStore, type Selection } from "../store";
import { useCatalog } from "../api";
import type { Graph, GraphIssue, NodeKind, WfEdge, WfNode } from "../types";

export interface CanvasOverlay {
  /** Diff status by stable id (nodes and edges). */
  status?: Map<string, DiffStatus>;
  ghostNodes?: WfNode[];
  ghostEdges?: WfEdge[];
  /** Running cases per state key (live heatmap). */
  heat?: Record<string, number>;
  simCurrent?: string | null;
  simVisited?: string[];
  simEdges?: string[];
}

export const DRAG_MIME = "application/ncp-builder";
export type PaletteItem = { type: "node"; kind: NodeKind; label?: string } | { type: "action"; actionType: string } | { type: "trigger"; kind: "schedule" | "event" | "manual" | "eservice" };

const nodeTypes = { builder: BuilderNode };
const edgeTypes = { builder: BuilderEdge };

interface Props {
  /** The graph to draw; defaults to the one being edited. */
  graph?: Graph;
  editable: boolean;
  overlay?: CanvasOverlay;
  issues?: GraphIssue[];
  showLanes?: boolean;
  onOpenHistory?: (nodeId: string) => void;
  onFocusInspector?: (tab: string) => void;
  className?: string;
  minimap?: boolean;
}

let clipboard: { nodes: WfNode[]; edges: WfEdge[] } | null = null;

/**
 * The editable workflow canvas: drag steps from the palette (or onto a transition to insert them), draw transitions between handles,
 * move and multi-select states, right-click for actions, double-click to rename. Overlays show diffs, the Copilot's proposal, the live
 * heatmap and the simulator's position.
 */
export function BuilderCanvas({ graph: given, editable, overlay, issues = [], showLanes, onOpenHistory, onFocusInspector, className, minimap = true }: Props) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const store = useStore();
  const edited = useBuilder((s) => s.graph);
  const storeSelection = useBuilder((s) => s.selection);
  // A canvas given its own graph (history, diff, merge preview) keeps its own selection.
  const detached = !!given;
  const [localSelection, setLocalSelection] = useState<Selection>({ nodes: [], edges: [] });
  const localRef = useRef(localSelection);
  localRef.current = localSelection;
  const selection = detached ? localSelection : storeSelection;
  const currentSelection = useCallback(() => (detached ? localRef.current : store.getState().selection), [detached, store]);
  const select = useCallback((s: Selection) => (detached ? setLocalSelection(s) : store.select(s)), [detached, store]);
  const graph = given ?? edited;
  const flow = useReactFlow();
  const catalog = useCatalog();
  const wrapper = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  // Controlled flow: React Flow reports node sizes as changes; keep them so nodes render measured.
  const [measured, setMeasured] = useState<Map<string, { width: number; height: number }>>(() => new Map());

  const roleLabel = useCallback((r: string) => t(`roles.${r}`, { defaultValue: r }), [t]);
  const agentName = useCallback((k: string) => { const a = catalog.data?.agents.find((x) => x.key === k); return (locale === "ar" ? a?.nameAr : a?.name) ?? k; }, [catalog.data, locale]);
  const onRename = useCallback((id: string, label: string) => store.update("Rename", (g) => { const n = g.nodes.find((x) => x.id === id); if (n) n.label = label; }), [store]);

  const byKey = useMemo(() => new Map(graph.nodes.map((n) => [n.key, n])), [graph]);
  const allNodes = useMemo(() => [...graph.nodes, ...(overlay?.ghostNodes ?? []).filter((g) => !graph.nodes.some((n) => n.id === g.id))], [graph, overlay?.ghostNodes]);
  const idOfKey = useMemo(() => new Map(allNodes.map((n) => [n.key, n.id!])), [allNodes]);
  const heatMax = useMemo(() => Math.max(0, ...Object.values(overlay?.heat ?? {})), [overlay?.heat]);

  const rfNodes = useMemo<Node<BuilderNodeData>[]>(() => allNodes.map((n) => {
    const ghost = !graph.nodes.includes(n);
    return {
      id: n.id!, type: "builder", position: n.position ?? { x: 0, y: 0 }, draggable: editable && !ghost, selectable: !ghost, connectable: editable && !ghost,
      selected: selection.nodes.includes(n.id!), zIndex: ghost ? 0 : 1, measured: measured.get(n.id!),
      data: {
        node: n, isStart: n.key === graph.initialState, issues: issues.filter((i) => i.node === n.key && !i.edge), diff: ghost ? "removed" : overlay?.status?.get(n.id!), ghost,
        heat: overlay?.heat?.[n.key], heatMax, sim: overlay?.simCurrent === n.key ? "current" : overlay?.simVisited?.includes(n.key) ? "visited" : undefined,
        locale, roleLabel, agentName, editable, onRename,
      },
    };
  }), [allNodes, graph, editable, selection.nodes, measured, issues, overlay, heatMax, locale, roleLabel, agentName, onRename]);

  const onInsert = useCallback((edgeId: string, x: number, y: number) => {
    const bounds = wrapper.current?.getBoundingClientRect();
    setMenu({ kind: "insert", id: edgeId, x: x - (bounds?.left ?? 0), y: y - (bounds?.top ?? 0) });
  }, []);

  const rfEdges = useMemo<Edge<BuilderEdgeData>[]>(() => {
    const ghostEdges = (overlay?.ghostEdges ?? []).filter((g) => !graph.edges.some((e) => e.id === g.id));
    return [...graph.edges, ...ghostEdges].flatMap((e) => {
      const source = idOfKey.get(e.from);
      const target = idOfKey.get(e.to);
      if (!source || !target) return [];
      const ghost = !graph.edges.includes(e);
      const status = ghost ? "removed" : overlay?.status?.get(e.id!);
      return [{
        id: e.id!, source, target, type: "builder", selected: selection.edges.includes(e.id!), selectable: !ghost, reconnectable: editable && !ghost,
        markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: status === "added" ? "#16a34a" : status === "removed" ? "#f87171" : e.outcome === "approved" ? "#16a34a" : "#b8ad98" },
        data: { edge: e, diff: status, ghost, sim: overlay?.simEdges?.includes(e.id!), locale, editable, onInsert: editable ? onInsert : undefined },
      }];
    });
  }, [graph, overlay, idOfKey, selection.edges, editable, locale, onInsert]);

  // ---------------------------------------------------------------- editing

  const onNodesChange = useCallback((changes: NodeChange<Node<BuilderNodeData>>[]) => {
    const sizes = changes.filter((c) => c.type === "dimensions" && c.dimensions);
    if (sizes.length) setMeasured((prev) => { const next = new Map(prev); for (const c of sizes) if (c.type === "dimensions" && c.dimensions) next.set(c.id, c.dimensions); return next; });
    const picks = changes.filter((c) => c.type === "select");
    if (picks.length) {
      const current = new Set(currentSelection().nodes);
      for (const c of picks) if (c.type === "select") { if (c.selected) current.add(c.id); else current.delete(c.id); }
      select({ nodes: [...current], edges: currentSelection().edges });
    }
    if (!editable) return;
    const moves = changes.filter((c): c is NodePositionChange => c.type === "position" && !!c.position);
    if (moves.length) {
      const done = moves.some((c) => c.dragging === false);
      store.update("Move", (g) => {
        for (const c of moves) {
          if (!c.position) continue;
          const n = g.nodes.find((x) => x.id === c.id);
          if (!n) continue;
          n.position = done ? snap(c.position) : c.position;
          if (showLanes && done) n.lane = laneOf(n.position.y);
        }
      }, `move:${moves.map((m) => m.id).join()}`);
    }
  }, [editable, store, showLanes, currentSelection, select]);

  const onEdgesChange = useCallback((changes: EdgeChange<Edge<BuilderEdgeData>>[]) => {
    const picks = changes.filter((c) => c.type === "select");
    if (!picks.length) return;
    const current = new Set(currentSelection().edges);
    for (const c of picks) if (c.type === "select") { if (c.selected) current.add(c.id); else current.delete(c.id); }
    select({ nodes: currentSelection().nodes, edges: [...current] });
  }, [currentSelection, select]);

  const onConnect = useCallback((c: Connection) => {
    if (!editable) return;
    store.update("Connect", (g) => {
      const from = g.nodes.find((n) => n.id === c.source);
      const to = g.nodes.find((n) => n.id === c.target);
      if (!from || !to) return;
      const edge = newEdge(g, from, to);
      g.edges.push(edge);
      queueMicrotask(() => { store.select({ nodes: [], edges: [edge.id!] }); onFocusInspector?.("general"); });
    });
  }, [editable, store, onFocusInspector]);

  const onReconnect = useCallback((old: Edge, c: Connection) => {
    store.update("Reconnect", (g) => {
      const e = g.edges.find((x) => x.id === old.id);
      const from = g.nodes.find((n) => n.id === c.source);
      const to = g.nodes.find((n) => n.id === c.target);
      if (e && from && to) { e.from = from.key; e.to = to.key; }
    });
  }, [store]);

  const onDelete = useCallback(({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) => {
    if (!editable) return;
    const ids = new Set(nodes.map((n) => n.id));
    const start = graph.nodes.find((n) => n.key === graph.initialState);
    if (start && ids.has(start.id!)) { toast.error(t("admin:builder.canvas.cannotDeleteStart")); ids.delete(start.id!); }
    const edgeIds = new Set(edges.map((e) => e.id));
    store.update("Delete", (g) => {
      const keys = new Set(g.nodes.filter((n) => ids.has(n.id!)).map((n) => n.key));
      g.nodes = g.nodes.filter((n) => !keys.has(n.key));
      g.edges = g.edges.filter((e) => !keys.has(e.from) && !keys.has(e.to) && !edgeIds.has(e.id!));
    });
    store.select({ nodes: [], edges: [] });
  }, [editable, graph, store, t]);

  // ---------------------------------------------------------------- palette drops

  const nearestEdge = useCallback((p: { x: number; y: number }) => {
    let best: { id: string; d: number } | null = null;
    for (const e of graph.edges) {
      const a = byKey.get(e.from)?.position; const b = byKey.get(e.to)?.position;
      if (!a || !b) continue;
      const mx = (a.x + NODE_W + b.x) / 2; const my = (a.y + b.y) / 2 + NODE_H / 2;
      const d = Math.hypot(mx - p.x, my - p.y);
      if (d < 70 && (!best || d < best.d)) best = { id: e.id!, d };
    }
    return best?.id ?? null;
  }, [graph, byKey]);

  const nodeAt = useCallback((p: { x: number; y: number }) => graph.nodes.find((n) => n.position && p.x >= n.position.x && p.x <= n.position.x + NODE_W && p.y >= n.position.y && p.y <= n.position.y + NODE_H + 30), [graph]);

  const onDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    if (!editable) return;
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    const item = JSON.parse(raw) as PaletteItem;
    const point = flow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    if (item.type === "node") {
      const edgeId = nearestEdge(point);
      store.update(edgeId ? "Insert step" : "Add step", (g) => {
        const node = newNode(g, item.kind, { x: point.x - NODE_W / 2, y: point.y - NODE_H / 2 }, item.label);
        if (item.kind === "Start" && !g.initialState) g.initialState = node.key;
        if (edgeId) insertOnEdge(g, edgeId, node); else g.nodes.push(node);
        queueMicrotask(() => store.select({ nodes: [node.id!], edges: [] }));
      });
      return;
    }
    if (item.type === "action") {
      const target = nodeAt(point);
      if (!target) { toast.message(t("admin:builder.canvas.dropActionOnStep")); return; }
      store.update("Add action", (g) => { const n = g.nodes.find((x) => x.id === target.id); if (n) (n.onEnter ??= []).push({ type: item.actionType, config: {} }); });
      store.select({ nodes: [target.id!], edges: [] });
      onFocusInspector?.("actions");
      return;
    }
    store.update("Add start trigger", (g) => {
      const start = g.nodes.find((n) => n.key === g.initialState);
      if (start) (start.triggers ??= []).push({ kind: item.kind, label: null, enabled: true, schedule: item.kind === "schedule" ? "0 8 1 * *" : null, event: null });
      if (start) queueMicrotask(() => store.select({ nodes: [start.id!], edges: [] }));
    });
    onFocusInspector?.("triggers");
  }, [editable, flow, nearestEdge, nodeAt, store, t, onFocusInspector]);

  // ---------------------------------------------------------------- keyboard

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).closest("input,textarea,select,[contenteditable=true]")) return;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); if (e.shiftKey) store.redo(); else store.undo(); return; }
    if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); store.redo(); return; }
    if (mod && e.key.toLowerCase() === "a") { e.preventDefault(); select({ nodes: graph.nodes.map((n) => n.id!), edges: [] }); return; }
    if (!editable) return;
    if (mod && e.key.toLowerCase() === "c") { copy(graph, selection.nodes); return; }
    if (mod && e.key.toLowerCase() === "v") { e.preventDefault(); paste(store); return; }
    if (mod && e.key.toLowerCase() === "d") { e.preventDefault(); copy(graph, selection.nodes); paste(store); }
  }, [graph, selection.nodes, store, editable, select]);

  // Open on the beginning of the flow at a readable zoom; the minimap shows the whole of a long workflow.
  const initialFit = useMemo(() => {
    const ordered = [...graph.nodes].filter((n) => n.position).sort((a, b) => a.position!.x - b.position!.x);
    const first = ordered.length > 10 ? ordered.filter((n) => n.position!.x <= ordered[8]!.position!.x) : ordered;
    return { padding: 0.12, maxZoom: 1, minZoom: 0.35, nodes: first.map((n) => ({ id: n.id! })) };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const lanes = graph.lanes?.length ? graph.lanes : DEFAULT_LANES;
  const laneCount = Math.max(lanes.length, ...graph.nodes.map((n) => n.lane + 1));
  const xs = graph.nodes.map((n) => n.position?.x ?? 0);
  const minX = Math.min(0, ...xs) - 400;
  const maxX = Math.max(1200, ...xs) + NODE_W + 600;

  return (
    <div ref={wrapper} className={className ?? "relative h-full w-full"} onKeyDown={onKeyDown} tabIndex={0} dir="ltr" onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }} onDrop={onDrop}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onReconnect={onReconnect}
        onDelete={onDelete}
        onNodeContextMenu={(e, n) => { e.preventDefault(); const b = wrapper.current!.getBoundingClientRect(); setMenu({ kind: "node", id: n.id, x: e.clientX - b.left, y: e.clientY - b.top }); }}
        onEdgeContextMenu={(e, ed) => { e.preventDefault(); const b = wrapper.current!.getBoundingClientRect(); setMenu({ kind: "edge", id: ed.id, x: e.clientX - b.left, y: e.clientY - b.top }); }}
        onPaneContextMenu={(e) => { e.preventDefault(); const b = wrapper.current!.getBoundingClientRect(); setMenu({ kind: "pane", id: "", x: e.clientX - b.left, y: e.clientY - b.top, flow: flow.screenToFlowPosition({ x: e.clientX, y: e.clientY }) }); }}
        onPaneClick={() => setMenu(null)}
        deleteKeyCode={editable ? ["Backspace", "Delete"] : null}
        multiSelectionKeyCode={["Meta", "Control", "Shift"]}
        selectionKeyCode="Shift"
        nodesDraggable={editable}
        nodesConnectable={editable}
        edgesReconnectable={editable}
        snapToGrid
        snapGrid={[GRID, GRID]}
        connectionLineType={ConnectionLineType.SmoothStep}
        connectionLineStyle={{ stroke: "var(--color-aegold-500)", strokeWidth: 2 }}
        defaultEdgeOptions={{ type: "builder" }}
        fitView
        fitViewOptions={initialFit}
        minZoom={0.15}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
        elevateEdgesOnSelect
      >
        {showLanes ? (
          <ViewportPortal>
            {Array.from({ length: laneCount }, (_, i) => (
              <LaneBand key={i} top={i * LANE_H} height={LANE_H} left={minX} width={maxX - minX} labelOffset={Math.min(...xs, 0) - minX - 200} label={(locale === "ar" ? lanes[i]?.labelAr : lanes[i]?.label) ?? lanes[i]?.label ?? `Lane ${i + 1}`} />
            ))}
          </ViewportPortal>
        ) : null}
        <Background variant={BackgroundVariant.Dots} color="#d9d0bd" gap={GRID} size={1.4} />
        <Controls showInteractive={false} position="bottom-left" className="!rounded-lg !border !border-sand-200 !shadow-sand" />
        {minimap ? <MiniMap pannable zoomable position="bottom-right" className="!rounded-xl !border !border-sand-200 !bg-white/90 !shadow-sand" nodeColor={(n) => (n.data as BuilderNodeData).diff === "added" ? "#16a34a" : (n.data as BuilderNodeData).diff === "removed" ? "#f87171" : (n.data as BuilderNodeData).diff === "modified" ? "#d97706" : (n.data as BuilderNodeData).heat ? "#b68a35" : "#cfc5b1"} maskColor="rgb(250 247 240 / 0.7)" /> : null}
      </ReactFlow>
      {menu ? <CanvasMenu menu={menu} graph={graph} editable={editable} onClose={() => setMenu(null)} onOpenHistory={onOpenHistory} onFocusInspector={onFocusInspector} /> : null}
    </div>
  );
}

function copy(graph: Graph, ids: string[]) {
  const nodes = graph.nodes.filter((n) => ids.includes(n.id!));
  if (nodes.length === 0) return;
  const keys = new Set(nodes.map((n) => n.key));
  clipboard = { nodes: structuredClone(nodes), edges: structuredClone(graph.edges.filter((e) => keys.has(e.from) && keys.has(e.to))) };
}

function paste(store: ReturnType<typeof useStore>) {
  const data = clipboard;
  if (!data) return;
  const created: string[] = [];
  store.update("Paste", (g) => {
    const rename = new Map<string, string>();
    for (const n of data.nodes) {
      const copy = newNode(g, n.kind, { x: (n.position?.x ?? 0) + 40, y: (n.position?.y ?? 0) + 40 }, n.label);
      Object.assign(copy, { ...structuredClone(n), id: copy.id, key: copy.key, position: copy.position });
      rename.set(n.key, copy.key);
      g.nodes.push(copy);
      created.push(copy.id!);
    }
    for (const e of data.edges) g.edges.push({ ...structuredClone(e), id: `e-${Math.random().toString(36).slice(2, 9)}`, from: rename.get(e.from)!, to: rename.get(e.to)! });
  });
  store.select({ nodes: created, edges: [] });
}
